"use client";

import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from "react";

type ImageryRequestsIndexChromeContextType = {
	/**
	 * When true, `ImageryAppShell` hides the fixed chrome header on the exact
	 * `/imagery/requests` route (check-in form or session bootstrap only).
	 */
	hideShellHeaderOnRequestsIndex: boolean;
	setHideShellHeaderOnRequestsIndex: (hide: boolean) => void;
};

const ImageryRequestsIndexChromeContext =
	createContext<ImageryRequestsIndexChromeContextType | null>(null);

export function ImageryRequestsIndexChromeProvider({
	children,
}: {
	children: ReactNode;
}) {
	const [hideShellHeaderOnRequestsIndex, setHideShellHeaderOnRequestsIndex] =
		useState(false);

	const setHideShellHeaderOnRequestsIndexStable = useCallback(
		(hide: boolean) => {
			setHideShellHeaderOnRequestsIndex(hide);
		},
		[]
	);

	const value = useMemo(
		() => ({
			hideShellHeaderOnRequestsIndex,
			setHideShellHeaderOnRequestsIndex:
				setHideShellHeaderOnRequestsIndexStable,
		}),
		[
			hideShellHeaderOnRequestsIndex,
			setHideShellHeaderOnRequestsIndexStable,
		]
	);

	return (
		<ImageryRequestsIndexChromeContext.Provider value={value}>
			{children}
		</ImageryRequestsIndexChromeContext.Provider>
	);
}

export function useImageryRequestsIndexChrome(): ImageryRequestsIndexChromeContextType {
	const ctx = useContext(ImageryRequestsIndexChromeContext);
	if (!ctx) {
		throw new Error(
			"useImageryRequestsIndexChrome must be used within ImageryRequestsIndexChromeProvider"
		);
	}
	return ctx;
}
